<script setup lang="ts">
/*
 * Primary nav pills — ported markup + ordering from the V6 prototype
 * (lines ~9264–9276 of `redesign/optimal-hr-prototype-v6.html`):
 *   Home · People · HR Ops · Payroll · Work Mgmt · Performance ·
 *   Field Ops · Insights · More Apps
 *
 * Home's pill (and route) is hidden in production only — see
 * `src/config/prod-hidden.css` / `src/config/featureFlags.ts` — dev/staging
 * keep it as the default landing page.
 *
 * Labels match the proto verbatim (no abbreviations, no NEW badges —
 * the v2 build used to mark Perform + Field Ops as NEW; the proto
 * removed those badges). The Admin route is repurposed as the proto's
 * "More Apps" pill (route name + path stay `admin` / `/admin`; only the
 * label + `meta.nav` differ — see `src/router/index.ts`).
 *
 * Styling lives in `./shell-styles.css` (`.nav-pill` + `.active`
 * variants). The `#primaryNav` id matches the proto so the
 * horizontal-scroll behaviour at narrow widths applies.
 */
import { useRoute, RouterLink } from 'vue-router';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { useAuthStore } from '@/stores/auth';

interface NavItem {
  routeName: string;
  navKey: string;
  labelKey: string;
  /**
   * Optional visibility predicate. When present, the pill renders only if it
   * returns true (e.g. Billing is provider-persona only). Pills without a
   * predicate are always shown.
   */
  show?: () => boolean;
}

const auth = useAuthStore();

// Order locked to the proto. `navKey` mirrors `route.meta.nav`; child
// routes under each module set the same key so the parent pill stays
// active (e.g. `/pay/run/123` keeps "Payroll" highlighted). Billing sits
// between Payroll and Work Mgmt (proto L9365) and is provider-gated.
const items: NavItem[] = [
  { routeName: 'home', navKey: 'home', labelKey: 'v2.shell.nav.home' },
  { routeName: 'people', navKey: 'people', labelKey: 'v2.shell.nav.people' },
  {
    routeName: 'organization',
    navKey: 'organization',
    labelKey: 'v2.shell.nav.organization',
  },
  { routeName: 'work', navKey: 'work', labelKey: 'v2.shell.nav.work' },
  { routeName: 'pay', navKey: 'pay', labelKey: 'v2.shell.nav.pay' },
  {
    routeName: 'billing',
    navKey: 'billing',
    labelKey: 'v2.shell.nav.billing',
    show: () => auth.isProvider,
  },
  { routeName: 'workmgmt', navKey: 'workmgmt', labelKey: 'v2.shell.nav.workmgmt' },
  { routeName: 'perform', navKey: 'perform', labelKey: 'v2.shell.nav.perform' },
  { routeName: 'fieldops', navKey: 'fieldops', labelKey: 'v2.shell.nav.fieldops' },
  { routeName: 'insights', navKey: 'insights', labelKey: 'v2.shell.nav.insights' },
  { routeName: 'admin', navKey: 'moreapps', labelKey: 'v2.shell.nav.moreapps' },
];

const route = useRoute();
const { t } = useI18n();
const activeNav = computed(() => (route.meta['nav'] as string | undefined) ?? route.name);
const visibleItems = computed(() => items.filter((i) => !i.show || i.show()));
</script>

<template>
  <nav
    id="primaryNav"
    class="flex min-w-0 flex-1 items-center justify-center gap-0.5"
    aria-label="Main"
  >
    <RouterLink
      v-for="item in visibleItems"
      :key="item.routeName"
      :to="{ name: item.routeName }"
      :class="['nav-pill', { active: activeNav === item.navKey }]"
      :aria-current="activeNav === item.navKey ? 'page' : undefined"
      :data-testid="`nav-pill-${item.navKey}`"
    >
      {{ t(item.labelKey) }}
    </RouterLink>
  </nav>
</template>
